// PaginatedDataGrid.tsx - Community Version
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridSortModel,
  GridRowSelectionModel,
  GridPaginationModel,
  GridRowModesModel,
  GridRowModes,
  GridRowId,
  GridToolbar,
  GridActionsCellItem,
  GridEventListener,
  GridRowEditStopReasons,
  GridValidRowModel,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
  gridClasses,
} from '@mui/x-data-grid';
import {
  Box,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
  Typography,
  Chip,
  LinearProgress,
  Stack,
  useTheme,
  alpha,
  CircularProgress,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  ErrorOutline,
} from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { formatCurrencyNumber, formatDate, formatDateTime, formatIntPhoneNumber } from '../../helpers/formatting-utils';
import dayjs from 'dayjs';
import { colorParser } from './ColorParser';

// Interfaces (same as before)
export interface PaginatedDataGridColumn extends Omit<GridColDef, 'renderCell'> {
  renderCell?: (params: GridRenderCellParams) => React.ReactNode;
  exportable?: boolean;
  searchable?: boolean;
  required?: boolean;
  validation?: (value: any) => string | null;
  columnType?: 'color' | 'chip' | 'progress' | 'phone' | 'currency' | 'barcode';
  chipColors?: Record<string, { backgroundColor: string; color: string }>;
  progressConfig?: {
    expectedField: string;
    actualField: string;
    dueDateField?: string;
  };
  barcodeConfig?: {
    format?: string;
    width?: number;
    height?: number;
  };
}

export interface PaginatedDataGridFilter {
  id: string;
  label: string;
  type: 'select' | 'multiselect' | 'text' | 'number' | 'date' | 'daterange' | 'boolean';
  column: string;
  options?: Array<{ value: any; label: string }> | ((data: any[]) => Array<{ value: any; label: string }>);
  defaultValue?: any;
}

export interface PaginatedDataGridAction {
  key: string;
  title: string;
  icon?: React.ReactNode;
  onClick: (selectedIds: GridRowId[]) => void | Promise<void>;
  disabled?: (selectedIds: GridRowId[]) => boolean;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  confirmDialog?: {
    title: string;
    message: string | ((count: number) => string);
  };
  visible?: (selectedIds: GridRowId[]) => boolean;
}

export interface BackendPaginationResponse {
  items?: any[];
  data?: any[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface PaginatedDataGridProps {
  title?: string;
  subtitle?: string;
  columns: PaginatedDataGridColumn[];
  rows?: any[];
  idField?: string;
  loading?: boolean;
  error?: Error | null;
  emptyMessage?: string;
  useBackendPagination?: boolean;
  apiEndpoint?: string;
  onDataFetch?: (params: {
    page: number;
    pageSize: number;
    sortModel?: GridSortModel;
    filterModel?: GridFilterModel;
    search?: string;
  }) => Promise<BackendPaginationResponse>;
  cacheTimeout?: number;
  filters?: PaginatedDataGridFilter[];
  actions?: PaginatedDataGridAction[];
  rowActions?: Array<{
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: (id: GridRowId, row: any) => void;
    visible?: (row: any) => boolean;
    disabled?: (row: any) => boolean;
    color?: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  }>;
  onAddRow?: (row: any) => Promise<any>;
  onEditRow?: (row: any) => Promise<any>;
  onDeleteRow?: (id: GridRowId) => Promise<void>;
  addRowFields?: string[];
  wmsMode?: boolean;
  densityLevel?: 'comfortable' | 'standard' | 'compact';
  striped?: boolean;
  collapsible?: boolean;
  maxHeight?: string | number;
  onRowClick?: (params: any) => void;
  onSelectionChange?: (selection: GridRowId[]) => void;
  enableGrouping?: boolean;
  enableAggregation?: boolean;
  enablePivot?: boolean;
  initialState?: any;
  sx?: any;
  className?: string;
}

// DRAMATIC & MODERN Table Styles 🎨🚀
const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    position: 'relative',
  },
  container: {
    width: '100%',
    background: theme.palette.background.paper,
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: 'linear-gradient(90deg, #FF6B9D 0%, #FFA952 50%, #A770EF 100%)',
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    '&:hover': {
      boxShadow: '0 16px 48px rgba(0, 0, 0, 0.16)',
      transform: 'translateY(-2px)',
      '&::before': {
        opacity: 1,
      },
    },
  },
  wmsContainer: {
    backgroundColor: theme.palette.background.paper,
    border: `2px solid ${theme.palette.divider}`,
  },
  header: {
    padding: theme.spacing(3, 4),
    background: 'linear-gradient(135deg, rgba(255, 107, 157, 0.08) 0%, rgba(167, 112, 239, 0.08) 100%)',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'default',
    transition: 'all 0.3s ease',
  },
  headerClickable: {
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, rgba(255, 107, 157, 0.12) 0%, rgba(167, 112, 239, 0.12) 100%)',
      transform: 'translateY(-1px)',
    },
  },
  titleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 800,
    background: 'linear-gradient(135deg, #FF6B9D 0%, #A770EF 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '0.9375rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
  },
  dataGrid: {
    border: 'none',
    '& .MuiDataGrid-columnHeaders': {
      background: 'linear-gradient(135deg, rgba(255, 107, 157, 0.05) 0%, rgba(167, 112, 239, 0.05) 100%)',
      borderBottom: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      minHeight: '60px !important',
      maxHeight: '60px !important',
      lineHeight: '60px !important',
    },
    '& .MuiDataGrid-columnHeader': {
      padding: theme.spacing(0, 2),
      '&:focus': {
        outline: 'none',
      },
      '&:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 800,
      fontSize: '0.875rem',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: theme.palette.text.primary,
    },
    '& .MuiDataGrid-columnSeparator': {
      color: alpha(theme.palette.primary.main, 0.2),
    },
    '& .MuiDataGrid-row': {
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      '&:hover': {
        background: 'linear-gradient(90deg, rgba(255, 107, 157, 0.08) 0%, rgba(167, 112, 239, 0.08) 100%)',
        transform: 'scale(1.01)',
        boxShadow: '0 4px 12px rgba(255, 107, 157, 0.15)',
        zIndex: 1,
      },
      '&.Mui-selected': {
        background: 'linear-gradient(90deg, rgba(255, 107, 157, 0.12) 0%, rgba(167, 112, 239, 0.12) 100%)',
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        '&:hover': {
          background: 'linear-gradient(90deg, rgba(255, 107, 157, 0.16) 0%, rgba(167, 112, 239, 0.16) 100%)',
        },
      },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
      fontSize: '0.9375rem',
      padding: theme.spacing(2, 2),
      fontWeight: 500,
      '&:focus': {
        outline: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        outlineOffset: '-2px',
      },
      '&:focus-within': {
        outline: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        outlineOffset: '-2px',
      },
    },
    '& .MuiDataGrid-footerContainer': {
      borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      background: 'linear-gradient(135deg, rgba(255, 107, 157, 0.03) 0%, rgba(167, 112, 239, 0.03) 100%)',
      minHeight: '64px',
      padding: theme.spacing(1, 2),
    },
    '& .MuiTablePagination-root': {
      fontSize: '0.9375rem',
      fontWeight: 600,
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      fontWeight: 600,
    },
    '& .MuiCheckbox-root': {
      color: alpha(theme.palette.primary.main, 0.5),
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'scale(1.1)',
      },
      '&.Mui-checked': {
        color: theme.palette.primary.main,
        transform: 'scale(1.05)',
      },
    },
    '& .MuiDataGrid-virtualScroller': {
      '&::-webkit-scrollbar': {
        width: '10px',
        height: '10px',
      },
      '&::-webkit-scrollbar-track': {
        background: alpha(theme.palette.primary.main, 0.05),
        borderRadius: '10px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'linear-gradient(135deg, #FF6B9D 0%, #A770EF 100%)',
        borderRadius: '10px',
        '&:hover': {
          background: 'linear-gradient(135deg, #FF6B9D 0%, #FFA952 50%, #A770EF 100%)',
        },
      },
    },
  },
  wmsDataGrid: {
    '& .MuiDataGrid-row': {
      '&:nth-of-type(even)': {
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
      },
    },
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: alpha(theme.palette.background.paper, 0.95),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    backdropFilter: 'blur(8px)',
  },
  errorState: {
    padding: theme.spacing(6, 4),
    textAlign: 'center',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 360,
    padding: theme.spacing(6, 4),
    background: 'linear-gradient(135deg, rgba(255, 107, 157, 0.03) 0%, rgba(167, 112, 239, 0.03) 100%)',
    borderRadius: 16,
    margin: theme.spacing(4),
    border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

// Enhanced Custom Toolbar with Modern Styling
const CustomToolbar: React.FC<{
  actions?: PaginatedDataGridAction[];
  selectedRows: GridRowId[];
  onActionClick: (action: PaginatedDataGridAction) => void;
  onAddClick?: () => void;
  canAdd?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}> = ({ actions, selectedRows, onActionClick, onAddClick, canAdd, searchValue, onSearchChange }) => {
  const theme = useTheme();

  return (
    <GridToolbarContainer
      sx={{
        padding: theme.spacing(2, 2.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.02)'
          : 'rgba(0, 0, 0, 0.01)',
        gap: 1,
      }}
    >
      <GridToolbarColumnsButton
        sx={{
          fontSize: '0.813rem',
          fontWeight: 500,
        }}
      />
      <GridToolbarFilterButton
        sx={{
          fontSize: '0.813rem',
          fontWeight: 500,
        }}
      />
      <GridToolbarDensitySelector
        sx={{
          fontSize: '0.813rem',
          fontWeight: 500,
        }}
      />
      <GridToolbarExport
        sx={{
          fontSize: '0.813rem',
          fontWeight: 500,
        }}
      />
      <Box sx={{ flexGrow: 1 }} />
      {onSearchChange && (
        <TextField
          variant="outlined"
          placeholder="Search..."
          value={searchValue || ''}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          sx={{
            mr: 1.5,
            minWidth: 240,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '0.875rem',
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.02)',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
              },
              '&.Mui-focused': {
                backgroundColor: theme.palette.background.paper,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon
                sx={{
                  mr: 1,
                  color: theme.palette.action.active,
                  fontSize: '1.25rem',
                }}
              />
            ),
          }}
        />
      )}
      {canAdd && (
        <Button
          size="medium"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddClick}
          sx={{
            ml: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            px: 2.5,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
            },
          }}
        >
          Add Row
        </Button>
      )}
      {actions && (
        <>
          {actions
            .filter(action => !action.visible || action.visible(selectedRows))
            .map(action => (
              <Tooltip key={action.key} title={action.title} arrow>
                <span>
                  <Button
                    size="medium"
                    variant={action.color === 'primary' ? 'contained' : 'outlined'}
                    color={action.color || 'primary'}
                    onClick={() => onActionClick(action)}
                    disabled={action.disabled?.(selectedRows)}
                    startIcon={action.icon}
                    sx={{
                      ml: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      px: 2.5,
                      boxShadow: action.color === 'primary' ? 'none' : undefined,
                      '&:hover': {
                        boxShadow: action.color === 'primary'
                          ? '0 2px 8px rgba(25, 118, 210, 0.25)'
                          : undefined,
                      },
                    }}
                  >
                    {action.title}
                  </Button>
                </span>
              </Tooltip>
            ))}
        </>
      )}
    </GridToolbarContainer>
  );
};

// Main Component
const PaginatedDataGrid: React.FC<PaginatedDataGridProps> = ({
  title,
  subtitle,
  columns: userColumns,
  rows: localRows = [],
  idField = 'id',
  loading: externalLoading = false,
  error: externalError = null,
  emptyMessage = 'No data available',
  useBackendPagination = false,
  apiEndpoint,
  onDataFetch,
  cacheTimeout = 300000,
  filters = [],
  actions = [],
  rowActions = [],
  onAddRow,
  onEditRow,
  onDeleteRow,
  addRowFields,
  wmsMode = false,
  densityLevel = 'standard',
  striped = false,
  collapsible = false,
  maxHeight,
  onRowClick,
  onSelectionChange,
  initialState,
  sx,
  className,
}) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  
  // State
  const [rows, setRows] = useState<any[]>(localRows);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [collapsed, setCollapsed] = useState(false);
  const [cache, setCache] = useState<Record<string, any>>({});
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  
  // Local storage for persistence
  const storageKey = `datagrid-${title?.replace(/\s+/g, '-').toLowerCase()}-state`;
  const [savedState, setSavedState] = useLocalStorage(storageKey, {});
  
  // Debounce search value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500); // Wait 500ms after user stops typing
    
    return () => clearTimeout(timer);
  }, [searchValue]);
  
  // Generate cache key
  const getCacheKey = useCallback((params: any) => {
    return JSON.stringify(params);
  }, []);
  
  // Fetch data from backend
  const fetchData = useCallback(async () => {
    if (!useBackendPagination) return;
    
    const params = {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sortModel,
      filterModel,
      search: debouncedSearchValue,
    };
    
    const cacheKey = getCacheKey(params);
    
    // Check cache
    if (cache[cacheKey] && Date.now() - cache[cacheKey].timestamp < cacheTimeout) {
      setRows(cache[cacheKey].data);
      setTotalRowCount(cache[cacheKey].totalCount);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      let response: BackendPaginationResponse;
      
      if (onDataFetch) {
        response = await onDataFetch(params);
      } else if (apiEndpoint) {
        const queryParams = new URLSearchParams({
          PageNumber: String(paginationModel.page + 1),
          PageSize: String(paginationModel.pageSize),
        });
        
        // Add search param if exists
        if (debouncedSearchValue) {
          queryParams.append('SearchTerm', debouncedSearchValue);
          // Add searchable columns
          const searchableColumns = userColumns
            .filter(col => col.searchable !== false)
            .map(col => col.field);
          searchableColumns.forEach(field => {
            queryParams.append('SearchColumns', field);
          });
        }
        
        // Add sort params
        if (sortModel.length > 0) {
          queryParams.append('SortBy', sortModel[0].field);
          queryParams.append('SortDirection', sortModel[0].sort || 'asc');
        }
        
        // Add filter params
        filterModel.items.forEach(filter => {
          if (filter.value !== undefined && filter.value !== '') {
            queryParams.append(`filters[${filter.field}]`, String(filter.value));
          }
        });
        
        const apiResponse = await DataService.get(`${apiEndpoint}?${queryParams.toString()}`);
        
        if (!apiResponse.ok) {
          throw new Error('Failed to fetch data');
        }
        
        const jsonResponse = await apiResponse.json();
        
        // Handle nested response structure
        if (jsonResponse.success && jsonResponse.data) {
          const data = jsonResponse.data;
          
          // Convert items object to array if needed
          let items: any[] = [];
          if (data.items) {
            if (Array.isArray(data.items)) {
              items = data.items;
            } else if (typeof data.items === 'object') {
              // Convert object with IDs as keys to array
              items = Object.values(data.items);
            }
          }
          
          response = {
            items: items,
            totalCount: data.totalCount || items.length,
            pageNumber: data.pageNumber || paginationModel.page + 1,
            pageSize: data.pageSize || paginationModel.pageSize,
            totalPages: data.totalPages || Math.ceil((data.totalCount || items.length) / paginationModel.pageSize),
            hasNext: data.hasNext || false,
            hasPrevious: data.hasPrevious || false,
          };
        } else {
          // Fallback for direct response format
          response = jsonResponse;
        }
      } else {
        throw new Error('No API endpoint or fetch function provided');
      }
      
      const items = response.items || response.data || [];
      
      // Update cache
      setCache(prev => ({
        ...prev,
        [cacheKey]: {
          data: items,
          totalCount: response.totalCount,
          timestamp: Date.now(),
        },
      }));
      
      setRows(items);
      setTotalRowCount(response.totalCount);
    } catch (err) {
      setError(err as Error);
      dispatch(showAlert({ message: 'Failed to fetch data', type: 'error' }));
    } finally {
      setIsLoading(false);
    }
  }, [
    useBackendPagination,
    paginationModel,
    sortModel,
    filterModel,
    apiEndpoint,
    onDataFetch,
    cacheTimeout,
    getCacheKey,
    cache,
    dispatch,
    debouncedSearchValue,
    userColumns,
  ]);
  
  // Effect to fetch data when dependencies change
  useEffect(() => {
    if (useBackendPagination) {
      fetchData();
    } else {
      setRows(localRows);
      setTotalRowCount(localRows.length);
    }
  }, [useBackendPagination, fetchData, localRows]);
  
  // Clear cache when filters, sort, or search change
  useEffect(() => {
    if (useBackendPagination) {
      setCache({});
      // Reset to first page when search changes
      if (debouncedSearchValue !== '') {
        setPaginationModel(prev => ({ ...prev, page: 0 }));
      }
    }
  }, [sortModel, filterModel, useBackendPagination, debouncedSearchValue]);
  
  // Process columns
  const processedColumns = useMemo<GridColDef[]>(() => {
    const cols: GridColDef[] = userColumns.map(col => {
      const baseCol: GridColDef = {
        ...col,
        flex: col.flex || 1,
        minWidth: col.minWidth || 100,
      };
      
      // Handle custom column types
      switch (col.columnType) {
        case 'color':
          baseCol.renderCell = (params) => {
            const colorValue = colorParser(params.value);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: colorValue,
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: wmsMode ? '2px' : '50%',
                  }}
                />
                <Typography variant="body2">{params.value}</Typography>
              </Box>
            );
          };
          break;
          
        case 'chip':
          baseCol.renderCell = (params) => {
            const chipStyle = col.chipColors?.[params.value];
            return (
              <Chip
                label={params.value}
                size="small"
                style={{
                  backgroundColor: chipStyle?.backgroundColor,
                  color: chipStyle?.color,
                }}
              />
            );
          };
          break;
          
        case 'progress':
          baseCol.renderCell = (params) => {
            const config = col.progressConfig!;
            const expected = params.row[config.expectedField];
            const actual = params.row[config.actualField];
            const dueDate = config.dueDateField ? params.row[config.dueDateField] : undefined;
            const percent = expected > 0 ? Math.min((actual / expected) * 100, 100) : 0;
            
            let color: 'success' | 'error' | 'warning' = 'success';
            if (dueDate && dayjs().isAfter(dayjs(dueDate))) {
              color = 'warning';
            } else if (actual > expected) {
              color = 'error';
            }
            
            return (
              <Box sx={{ width: '100%' }}>
                <LinearProgress
                  variant="determinate"
                  value={percent}
                  color={color}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {actual} / {expected}
                </Typography>
              </Box>
            );
          };
          break;
          
        case 'phone':
          baseCol.valueFormatter = (params) => formatIntPhoneNumber(params.value);
          break;
          
        case 'currency':
          baseCol.valueFormatter = (params) => formatCurrencyNumber(params.value);
          break;
      }
      
      // Handle date/datetime columns
      if (col.type === 'date') {
        baseCol.valueFormatter = (params) => formatDate(params.value);
      } else if (col.type === 'dateTime') {
        baseCol.valueFormatter = (params) => formatDateTime(params.value);
      }
      
      return baseCol;
    });
    
    // Add actions column if needed
    if (rowActions.length > 0 || onEditRow || onDeleteRow) {
      cols.push({
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        cellClassName: 'actions',
        getActions: ({ id, row }) => {
          const actions: React.ReactElement[] = [];
          
          if (onEditRow) {
            actions.push(
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                onClick={() => handleEditClick(id)}
                color="inherit"
              />
            );
          }
          
          if (onDeleteRow) {
            actions.push(
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => handleDeleteClick(id)}
                color="inherit"
              />
            );
          }
          
          rowActions
            .filter(action => !action.visible || action.visible(row))
            .forEach(action => {
              actions.push(
                <GridActionsCellItem
                  icon={React.isValidElement(action.icon) ? action.icon : <span>{action.icon}</span>}
                  label={action.label}
                  onClick={() => action.onClick(id, row)}
                  color={action.color || 'inherit'}
                  disabled={action.disabled?.(row)}
                />
              );
            });
          
          return actions;
        },
      });
    }
    
    return cols;
  }, [userColumns, rowActions, onEditRow, onDeleteRow, theme, wmsMode]);
  
  // Handlers
  const handleEditClick = (id: GridRowId) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };
  
  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };
  
  const handleDeleteClick = async (id: GridRowId) => {
    if (window.confirm('Are you sure you want to delete this row?')) {
      try {
        if (onDeleteRow) {
          await onDeleteRow(id);
          dispatch(showAlert({ message: 'Row deleted successfully', type: 'success' }));
        }
        setRows(rows.filter((row) => row[idField] !== id));
      } catch (error: any) {
        dispatch(showAlert({ message: error.message || 'Failed to delete row', type: 'error' }));
      }
    }
  };
  
  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };
  
  const processRowUpdate = async (newRow: GridValidRowModel) => {
    try {
      if (onEditRow) {
        const updatedRow = await onEditRow(newRow);
        dispatch(showAlert({ message: 'Row updated successfully', type: 'success' }));
        if (useBackendPagination) {
          fetchData();
        }
        return updatedRow || newRow;
      }
      return newRow;
    } catch (error: any) {
      dispatch(showAlert({ message: error.message || 'Failed to update row', type: 'error' }));
      throw error;
    }
  };
  
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };
  
  const handleAddRow = async () => {
    const newId = Math.max(...rows.map(r => r[idField] || 0)) + 1;
    const newRow = { [idField]: newId };
    
    // Initialize with default values
    userColumns.forEach(col => {
      if (col.field !== idField) {
        newRow[col.field] = col.type === 'number' ? 0 : undefined;
      }
    });
    
    setRows([newRow, ...rows]);
    setRowModesModel({
      ...rowModesModel,
      [newId]: { mode: GridRowModes.Edit, fieldToFocus: userColumns[0].field },
    });
  };
  
  const handleActionClick = async (action: PaginatedDataGridAction) => {
    if (action.confirmDialog) {
      const message = typeof action.confirmDialog.message === 'function'
        ? action.confirmDialog.message(selectionModel.length)
        : action.confirmDialog.message;
        
      if (!window.confirm(message)) return;
    }
    
    try {
      await action.onClick(selectionModel);
      setSelectionModel([]);
      if (useBackendPagination) {
        fetchData();
      }
    } catch (error: any) {
      dispatch(showAlert({ message: error.message || 'Action failed', type: 'error' }));
    }
  };
  
  // Enhanced Render loading state
  if (externalLoading && !rows.length) {
    return (
      <Paper className={cx(classes.container, className)} sx={sx}>
        <Box
          className={classes.loadingOverlay}
          sx={{
            minHeight: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            Loading data...
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Enhanced Render error state
  if ((externalError || error) && !rows.length) {
    const currentError = externalError || error;
    return (
      <Paper className={cx(classes.container, className)} sx={sx}>
        <Box
          sx={{
            padding: theme.spacing(6, 3),
            textAlign: 'center',
            minHeight: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <ErrorIcon
              sx={{
                fontSize: 40,
                color: theme.palette.error.main,
              }}
            />
          </Box>
          <Typography
            variant="h5"
            gutterBottom
            color="text.primary"
            sx={{ fontWeight: 700, mb: 1, letterSpacing: '-0.02em' }}
          >
            Error Loading Data
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 500, lineHeight: 1.6 }}
          >
            {currentError?.message || 'An unexpected error occurred while loading the data. Please try again.'}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<RefreshIcon />}
            onClick={() => fetchData()}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
              },
            }}
          >
            Retry
          </Button>
        </Box>
      </Paper>
    );
  }
  
  const loading = useBackendPagination ? isLoading : externalLoading;

  // Calculate dynamic height based on row count
  const calculateDynamicHeight = useCallback(() => {
    if (maxHeight) return maxHeight;
    
    const rowCount = rows.length;
    
    // If no rows, use a minimal height
    if (rowCount === 0) {
      return 200;
    }
    
    // More accurate MUI DataGrid measurements
    const showToolbar = actions.length > 0 || onAddRow || (useBackendPagination && searchValue !== undefined);
    const toolbarHeight = showToolbar ? 56 : 0;
    const headerHeight = 56; // DataGrid column header height
    const showPagination = useBackendPagination || rowCount > (paginationModel.pageSize || 10);
    const paginationHeight = showPagination ? 52 : 0;
    const rowHeight = densityLevel === 'compact' ? 37 : densityLevel === 'comfortable' ? 53 : 42;
    const borderPadding = 4; // Border and minimal padding
    const maxVisibleRows = paginationModel.pageSize || 10;
    
    // Calculate height for visible rows (limited by page size)
    const visibleRows = Math.min(rowCount, maxVisibleRows);
    const calculatedHeight = toolbarHeight + headerHeight + (visibleRows * rowHeight) + paginationHeight + borderPadding;
    
    // Set a reasonable minimum that actually respects small row counts
    const minHeight = toolbarHeight + headerHeight + (1 * rowHeight) + borderPadding;
    
    return Math.max(calculatedHeight, minHeight);
  }, [rows.length, maxHeight, densityLevel, actions.length, onAddRow, searchValue, paginationModel.pageSize]);

  // Enhanced Render empty state with Modern Design
  const renderEmptyState = () => {
    const hasFilters = searchValue || (filterModel.items && filterModel.items.length > 0);
    const hasAddAction = actions.some(action => action.key === 'add');

    return (
      <Box className={classes.emptyState}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <ErrorOutline
            sx={{
              fontSize: 40,
              color: theme.palette.primary.main,
              opacity: 0.8,
            }}
          />
        </Box>
        <Typography
          variant="h5"
          gutterBottom
          color="text.primary"
          sx={{
            fontWeight: 700,
            mb: 1,
            letterSpacing: '-0.02em',
          }}
        >
          {emptyMessage}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: 4,
            textAlign: 'center',
            maxWidth: 400,
            lineHeight: 1.6,
          }}
        >
          {hasFilters
            ? "We couldn't find any results matching your criteria. Try adjusting your search or filters."
            : "There is currently no data to display. Get started by adding your first item."}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          {hasFilters && (
            <Button
              variant="outlined"
              size="large"
              onClick={() => {
                setSearchValue('');
                setFilterModel({ items: [] });
              }}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Clear Filters
            </Button>
          )}
          {onAddRow && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                },
              }}
            >
              Add New Item
            </Button>
          )}
          {hasAddAction && !onAddRow && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => {
                const addAction = actions.find(action => action.key === 'add');
                if (addAction) addAction.onClick([]);
              }}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                },
              }}
            >
              Add New Item
            </Button>
          )}
        </Box>
      </Box>
    );
  };
  
  return (
    <Paper
      className={cx(
        classes.container,
        wmsMode && classes.wmsContainer,
        className
      )}
      sx={sx}
    >
      {(title || subtitle) && (
        <Box
          className={cx(
            classes.header,
            collapsible && classes.headerClickable
          )}
          onClick={collapsible ? () => setCollapsed(!collapsed) : undefined}
        >
          <Box className={classes.titleSection}>
            {title && <Typography className={classes.title}>{title}</Typography>}
            {subtitle && <Typography className={classes.subtitle}>{subtitle}</Typography>}
          </Box>
          <Box className={classes.actions}>
            {collapsible && (
              <IconButton size="small">
                {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            )}
          </Box>
        </Box>
      )}
      
      {!collapsed && (
        <>
          {rows.length === 0 && !loading ? (
            renderEmptyState()
          ) : (
            <Box sx={{ height: calculateDynamicHeight(), width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={processedColumns}
                loading={loading}
                rowCount={useBackendPagination ? totalRowCount : undefined}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                paginationMode={useBackendPagination ? 'server' : 'client'}
                sortingMode={useBackendPagination ? 'server' : 'client'}
                filterMode={useBackendPagination ? 'server' : 'client'}
                onSortModelChange={setSortModel}
                onFilterModelChange={setFilterModel}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={(newSelection) => {
                  setSelectionModel(newSelection);
                  onSelectionChange?.(newSelection);
                }}
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={setRowModesModel}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={(error) => {
                  console.error('Row update error:', error);
                }}
                onRowClick={onRowClick}
                density={densityLevel}
                getRowId={(row) => row[idField]}
                className={cx(
                  classes.dataGrid,
                  wmsMode && classes.wmsDataGrid,
                  striped && 'striped-rows'
                )}
                initialState={initialState}
                slots={{
                  toolbar: (actions.length > 0 || onAddRow || (useBackendPagination && searchValue !== undefined)) ? CustomToolbar : undefined,
                }}
                slotProps={{
                  toolbar: {
                    actions,
                    selectedRows: selectionModel,
                    onActionClick: handleActionClick,
                    onAddClick: onAddRow ? handleAddRow : undefined,
                    canAdd: !!onAddRow,
                    searchValue: searchValue,
                    onSearchChange: useBackendPagination ? setSearchValue : undefined,
                  } as any,
                }}
                hideFooter={rows.length <= (paginationModel.pageSize || 10) && !useBackendPagination}
                sx={{
                  [`& .${gridClasses.row}.striped-rows:nth-of-type(even)`]: {
                    backgroundColor: theme.palette.mode === 'dark' ? '#ffffff05' : '#00000005',
                  },
                  // Remove extra padding when there's minimal content
                  '& .MuiDataGrid-main': {
                    overflow: rows.length <= 3 ? 'visible' : 'auto',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    minHeight: rows.length > 0 ? 'auto' : '100px',
                  },
                  ...sx,
                }}
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default PaginatedDataGrid;

// Export utility function for color parsing
export { colorParser };